import React from 'react';

/**
 * Error Panel component for displaying user-friendly error messages
 *
 * @param {Object} props - Component props
 * @param {string} props.message - Error message to display
 * @param {string} props.title - Optional title for the error panel
 * @param {React.ReactNode} props.children - Optional additional content
 * @param {string} props.variant - Optional variant (error, warning, info)
 * @returns {React.ReactElement} Error panel component
 */
const ErrorPanel = ({ message, title = 'Error', children, variant = 'error' }) => {
  // Determine styles based on variant
  const getStyles = () => {
    switch (variant) {
      case 'warning':
        return 'bg-yellow-50 text-yellow-800 border-yellow-200';
      case 'info':
        return 'bg-blue-50 text-blue-800 border-blue-200';
      case 'error':
      default:
        return 'bg-red-50 text-red-800 border-red-200';
    }
  };

  return (
    <div className={`rounded-lg p-4 border-2 ${getStyles()} my-4`}>
      <div className="flex items-center">
        {variant === 'error' && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        )}
        {variant === 'warning' && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        )}
        {variant === 'info' && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )}
        <h3 className="text-lg font-medium">{title}</h3>
      </div>
      <div className="mt-2">
        <p>{typeof message === 'string' ? message : (message?.message || 'An error occurred')}</p>
        {children}
      </div>
    </div>
  );
};

export default ErrorPanel;
